import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Ship } from 'lucide-react';
import L from 'leaflet';

export const VesselMap = ({ vessels, theme }) => {
  const [selectedVessel, setSelectedVessel] = useState(null);

  // Get vessel color based on status
  const getVesselColor = (status) => {
    switch (status) {
      case 'underway':
        return '#2EC7C9'; // teal (accent)
      case 'at anchor':
        return '#FFC107'; // warning
      case 'moored':
        return '#4CAF50'; // success
      default:
        return '#9E9E9E'; // muted
    }
  };

  // Create custom ship icon for each vessel
  const createShipIcon = (vessel) => {
    const color = getVesselColor(vessel.status);
    return L.divIcon({
      html: `<div style="color: ${color}; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); cursor: pointer;" class="vessel-marker">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
          <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
          <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
          <path d="M12 10v4"/>
          <path d="M12 2v3"/>
        </svg>
      </div>`,
      className: '',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  };

  // Calculate center from vessels or use default
  const center = vessels && vessels.length > 0
    ? [vessels[0].latitude, vessels[0].longitude]
    : [20, 0];

  return (
    <div className="relative" data-testid="vessel-map-container">
      <Card className="h-[500px] overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle>Live Vessel Tracking</CardTitle>
          <div className="flex gap-4 mt-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2EC7C9' }}></div>
              <span>Underway</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFC107' }}></div>
              <span>At Anchor</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#4CAF50' }}></div>
              <span>Moored</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#9E9E9E' }}></div>
              <span>Other</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="h-[400px] relative">
            <MapContainer
              center={center}
              zoom={2}
              style={{ height: '100%', width: '100%' }}
              data-testid="leaflet-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url={
                  theme === 'dark'
                    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                }
              />
              {vessels.map((vessel) => (
                <Marker
                  key={vessel.id}
                  position={[vessel.latitude, vessel.longitude]}
                  icon={createShipIcon(vessel)}
                  eventHandlers={{
                    click: () => setSelectedVessel(vessel)
                  }}
                >
                  <Popup>
                    <div className="space-y-2 min-w-[180px]">
                      <div>
                        <div className="font-semibold text-sm">{vessel.name}</div>
                        <div className="text-xs text-gray-600" style={{ fontFamily: 'monospace' }}>
                          {vessel.imo}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-gray-600">Class</div>
                          <div className="font-medium">{vessel.vessel_class}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Speed</div>
                          <div className="font-medium">{vessel.speed} kn</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-gray-600">Status</div>
                          <div className="mt-1">
                            <span className="inline-block px-2 py-0.5 text-xs rounded border">
                              {vessel.status}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-gray-600">Destination</div>
                          <div className="font-medium">{vessel.destination}</div>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};